import axios from 'axios';
import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { baseAxiosConfig, applyVerticalOverrideInterceptor } from '../utils/apiConfig';

const playbookAxios = applyVerticalOverrideInterceptor(axios.create(baseAxiosConfig));

/**
 * Get all actions for the current user's vertical.
 * @param {Object} options - Optional filters
 * @param {string} options.status - Filter by status (pending, in_progress, completed, dismissed, snoozed)
 * @param {number} options.priority - Filter by priority (1-5)
 * @param {string} options.actionType - Filter by action type
 * @param {string} options.goalMetric - Filter by goal metric (review_frequency, total_reviews, traffic, sentiment)
 * @param {string} options.sortByGoal - Sort to show actions for this goal metric first
 * @returns {Promise<Array>} List of action items
 */
export async function getActions(options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const params = {
      user: user.email
    };

    // Add optional filters
    if (options.status) {
      params.status = options.status;
    }
    if (options.priority) {
      params.priority = options.priority;
    }
    if (options.actionType) {
      params.action_type = options.actionType;
    }
    if (options.goalMetric) {
      params.goal_metric = options.goalMetric;
    }
    if (options.sortByGoal) {
      params.sort_by_goal = options.sortByGoal;
    }

    const response = await playbookAxios.get('/playbook/actions/', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error) {
    logger.error("Error in getActions:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Get summary statistics for actions.
 * @returns {Promise<Object>} Actions summary
 */
export async function getActionsSummary() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.get('/playbook/actions/summary', {
      params: { user: user.email },
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getActionsSummary:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Update an action item.
 * @param {string} actionId - The action ID to update
 * @param {Object} updates - Fields to update
 * @param {string} updates.status - New status
 * @param {string} updates.snoozed_until - ISO date string for snooze
 * @param {number} updates.user_feedback_rating - Rating 1-5
 * @param {string} updates.user_feedback_text - Feedback text
 * @returns {Promise<Object>} Updated action
 */
export async function updateAction(actionId, updates) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.patch(
      `/playbook/actions/${actionId}`,
      updates,
      {
        params: { user: user.email },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    logger.error("Error in updateAction:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Get all reading items for the current user's vertical.
 * @param {Object} options - Optional filters
 * @param {boolean} options.read - Filter by read status
 * @param {string} options.feedback - Filter by feedback type
 * @param {string} options.scope - Filter by scope (business, location, industry)
 * @returns {Promise<Array>} List of reading items
 */
export async function getReadingItems(options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const params = {
      user: user.email
    };

    // Add optional filters
    if (options.read !== undefined) {
      params.read = options.read;
    }
    if (options.feedback) {
      params.feedback = options.feedback;
    }
    if (options.scope) {
      params.scope = options.scope;
    }
    
    const response = await playbookAxios.get('/playbook/reading-items/', {
      params,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    return response.data;
  } catch (error) {
    logger.error("Error in getReadingItems:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Get summary statistics for reading items.
 * @returns {Promise<Object>} Reading items summary
 */
export async function getReadingItemsSummary() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.get('/playbook/reading-items/summary', {
      params: { user: user.email },
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getReadingItemsSummary:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Update a reading item.
 * @param {string} readingId - The reading item ID to update
 * @param {Object} updates - Fields to update
 * @param {string} updates.read_at - ISO date string when read
 * @param {string} updates.user_feedback - Feedback (helpful, not_helpful, saved)
 * @returns {Promise<Object>} Updated reading item
 */
export async function updateReadingItem(readingId, updates) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.patch(
      `/playbook/reading-items/${readingId}`,
      updates,
      {
        params: { user: user.email },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    logger.error("Error in updateReadingItem:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Mark a reading item as read.
 * @param {string} readingId - The reading item ID
 * @returns {Promise<Object>} Result with reading_id and read_at
 */
export async function markReadingItemAsRead(readingId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.post(
      `/playbook/reading-items/${readingId}/mark-read`,
      {},
      {
        params: { user: user.email },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    logger.error("Error in markReadingItemAsRead:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Generate action items from review analysis using AI.
 * @param {Object} options - Generation options
 * @param {number} options.maxActions - Maximum number of actions to generate (1-20)
 * @param {boolean} options.replaceExisting - Whether to replace existing AI-generated actions
 * @returns {Promise<Object>} Result with actions_generated count
 */
export async function generateActionsFromReviews(options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const params = {
      user: user.email
    };

    if (options.maxActions) {
      params.max_actions = options.maxActions;
    }
    if (options.replaceExisting !== undefined) {
      params.replace_existing = options.replaceExisting;
    }
    
    const response = await playbookAxios.post(
      '/playbook/actions/generate',
      {},
      {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    logger.error("Error in generateActionsFromReviews:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || 'Server error');
    }
    throw error;
  }
}

/**
 * Fetch and store link preview (Open Graph image) for a reading item.
 * @param {string} readingId - The reading item ID
 * @returns {Promise<Object>} Result with preview_image URL
 */
export async function fetchReadingItemPreview(readingId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();
    
    const response = await playbookAxios.post(
      `/playbook/reading-items/${readingId}/fetch-preview`,
      {},
      {
        params: { user: user.email },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    return response.data;
  } catch (error) {
    logger.error("Error in fetchReadingItemPreview:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
    }
    // Don't throw - preview fetching is non-critical
    return { success: false, preview_image: null };
  }
}

const playbookService = {
  getActions,
  getActionsSummary,
  updateAction,
  generateActionsFromReviews,
  getReadingItems,
  getReadingItemsSummary,
  updateReadingItem,
  markReadingItemAsRead,
  fetchReadingItemPreview
};

export default playbookService;

